<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import CloseDispositionReportsAPI from 'dashboard/api/closeDispositionReports';

// Eltafouk-only Arabic-first page — all visible copy lives in this L object so the
// @intlify lint rule treats them as script data rather than bare template strings.
const L = {
  TITLE: 'تقرير إغلاق المحادثات',
  SUBTITLE:
    'العملاء بيتواصلوا ليه وبنقفل على إيه — من إجابات الموظفين عند حل المحادثة، بنفس مسميات الكول سنتر.',
  TZ_NOTE: 'بتوقيت القاهرة',
  FROM: 'من',
  TO: 'إلى',
  APPLY: 'تطبيق',
  LOADING: 'جاري التحميل…',
  EMPTY: 'مفيش إغلاقات في الفترة المختارة.',
  ALL_AGENTS: 'كل الموظفين',
  ALL_INBOXES: 'كل الصناديق',
  KPI_RESOLVES: 'إغلاقات',
  KPI_WITH: 'بتصنيف',
  KPI_AUTO: 'قفل تلقائي (ترحيب فقط)',
  KPI_COVERAGE: 'نسبة الالتزام',
  REASONS: 'العميل كان عايز إيه؟',
  REASONS_HINT: 'اضغط على أي سبب لفلترة التقرير كله بيه.',
  OUTCOMES: 'خلصت على إيه؟',
  CLASSIFICATIONS: 'مين اللي بيتواصل؟',
  AGENTS: 'التزام الموظفين',
  AGENTS_HINT: 'كل موظف قفل كام محادثة وكام واحدة فيهم بإجابات.',
  DAILY: 'الإغلاقات يوم بيوم',
  COL_AGENT: 'الموظف',
  COL_RESOLVES: 'إغلاقات',
  COL_WITH: 'بتصنيف',
  COL_RATE: 'الالتزام',
  CLEAR_FILTER: 'شيل الفلتر',
};

const PRESETS = [
  { key: 'today', label: 'اليوم' },
  { key: 'yesterday', label: 'أمس' },
  { key: 'week', days: 7, label: 'آخر 7 أيام' },
  { key: 'month', days: 30, label: 'آخر 30 يوم' },
];

const store = useStore();
const { t } = useI18n();

// Same i18n set the conversation-header chip and the close modal use — one vocabulary.
const classificationLabel = key => {
  const known = ['Student', 'Teacher', 'Library', 'Center', 'Other'];
  if (!known.includes(key)) return key;
  return t(`CONVERSATION.HEADER.ERP_CLASSIFICATIONS.${key.toUpperCase()}`);
};

const activePreset = ref('today');
const isCustom = ref(false);
const customFrom = ref('');
const customTo = ref('');
const selectedAgent = ref('');
const selectedInbox = ref('');
const reasonFilter = ref('');
const outcomeFilter = ref('');
const classificationFilter = ref('');

const report = ref(null);
const loading = ref(false);
const error = ref(null);

const agents = computed(() => store.getters['agents/getAgents'] || []);
const inboxes = computed(() => store.getters['inboxes/getInboxes'] || []);

const isoDate = d => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const rangeIso = computed(() => {
  const now = new Date();
  if (isCustom.value) {
    return {
      since: customFrom.value || isoDate(now),
      until: customTo.value || isoDate(now),
    };
  }
  const preset = PRESETS.find(p => p.key === activePreset.value);
  if (preset?.key === 'today')
    return { since: isoDate(now), until: isoDate(now) };
  if (preset?.key === 'yesterday') {
    const y = new Date(now - 86400000);
    return { since: isoDate(y), until: isoDate(y) };
  }
  return {
    since: isoDate(new Date(now - preset.days * 86400000)),
    until: isoDate(now),
  };
});

// Monotonic token: rapid filter clicks race their responses — only the newest wins.
let fetchToken = 0;
const fetchReport = async () => {
  fetchToken += 1;
  const token = fetchToken;
  loading.value = true;
  error.value = null;
  try {
    const { data } = await CloseDispositionReportsAPI.fetch({
      ...rangeIso.value,
      userIds: selectedAgent.value ? [selectedAgent.value] : null,
      inboxIds: selectedInbox.value ? [selectedInbox.value] : null,
      reason: reasonFilter.value || null,
      outcome: outcomeFilter.value || null,
      classification: classificationFilter.value || null,
    });
    if (token !== fetchToken) return;
    report.value = data;
  } catch (e) {
    if (token !== fetchToken) return;
    error.value = 'فشل التحميل — جرّب تاني أو حدّث الصفحة';
  } finally {
    if (token === fetchToken) loading.value = false;
  }
};

onMounted(() => {
  if (!agents.value.length) store.dispatch('agents/get');
  fetchReport();
});
watch(
  [
    activePreset,
    selectedAgent,
    selectedInbox,
    reasonFilter,
    outcomeFilter,
    classificationFilter,
  ],
  () => fetchReport()
);

const pickPreset = key => {
  const wasCustom = isCustom.value;
  isCustom.value = false;
  if (activePreset.value === key) {
    // Re-clicking the highlighted preset after a custom range: the watcher won't
    // fire (same value), so refetch explicitly.
    if (wasCustom) fetchReport();
  } else {
    activePreset.value = key;
  }
};
const applyCustom = () => {
  isCustom.value = true;
  fetchReport();
};

const summary = computed(() => report.value?.summary || null);
const byReason = computed(() => report.value?.by_reason || []);
const byOutcome = computed(() => report.value?.by_outcome || []);
const byClassification = computed(() => report.value?.by_classification || []);
const byAgent = computed(() => report.value?.by_agent || []);
const byDay = computed(() => report.value?.by_day || []);

const maxReason = computed(() =>
  Math.max(1, ...byReason.value.map(r => r.count))
);
const maxOutcome = computed(() =>
  Math.max(1, ...byOutcome.value.map(r => r.count))
);
const maxClassification = computed(() =>
  Math.max(1, ...byClassification.value.map(r => r.count))
);
const maxDay = computed(() => Math.max(1, ...byDay.value.map(r => r.count)));

const coverage = computed(() => {
  if (!summary.value?.resolves) return 0;
  return Math.round(
    (summary.value.with_disposition / summary.value.resolves) * 100
  );
});

const fmtInt = n => (n || 0).toLocaleString('en');
const rate = row =>
  row.resolves ? Math.round((row.with_disposition / row.resolves) * 100) : 0;

const hasDimensionFilter = computed(
  () => reasonFilter.value || outcomeFilter.value || classificationFilter.value
);
const clearDimensionFilters = () => {
  reasonFilter.value = '';
  outcomeFilter.value = '';
  classificationFilter.value = '';
};
</script>

<template>
  <div class="flex flex-col gap-4 p-6 mx-auto w-full max-w-6xl overflow-auto">
    <header class="flex flex-wrap gap-3 justify-between items-start">
      <div>
        <h1 class="text-xl font-semibold text-n-slate-12">{{ L.TITLE }}</h1>
        <p class="mt-0.5 text-[12.5px] text-n-slate-11">{{ L.SUBTITLE }}</p>
      </div>
      <span class="text-[12px] text-n-slate-10">{{ L.TZ_NOTE }}</span>
    </header>

    <!-- Filter bar -->
    <div
      class="flex flex-wrap items-center gap-x-4 gap-y-3 rounded-2xl border border-n-weak bg-n-solid-1 p-2.5 shadow-sm"
    >
      <div class="flex items-center gap-1 rounded-xl bg-n-alpha-1 p-1">
        <button
          v-for="p in PRESETS"
          :key="p.key"
          type="button"
          class="rounded-lg px-3 py-1.5 text-[12.5px] font-medium transition-all"
          :class="
            !isCustom && activePreset === p.key
              ? 'bg-n-brand text-white shadow-sm'
              : 'text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12'
          "
          @click="pickPreset(p.key)"
        >
          {{ p.label }}
        </button>
      </div>

      <div class="h-6 w-px bg-n-weak" />

      <div class="flex flex-wrap items-center gap-2">
        <span class="text-[12px] text-n-slate-10">{{ L.FROM }}</span>
        <input
          v-model="customFrom"
          type="date"
          dir="ltr"
          class="rounded-lg border border-n-weak bg-n-alpha-1 px-2.5 py-1.5 text-[12.5px] tabular-nums text-n-slate-12 outline-none focus:border-n-brand"
        />
        <span class="text-[12px] text-n-slate-10">{{ L.TO }}</span>
        <input
          v-model="customTo"
          type="date"
          dir="ltr"
          class="rounded-lg border border-n-weak bg-n-alpha-1 px-2.5 py-1.5 text-[12.5px] tabular-nums text-n-slate-12 outline-none focus:border-n-brand"
        />
        <button
          type="button"
          class="rounded-lg bg-n-brand px-3 py-1.5 text-[12.5px] font-medium text-white"
          @click="applyCustom"
        >
          {{ L.APPLY }}
        </button>
      </div>

      <div class="h-6 w-px bg-n-weak" />

      <select
        v-model="selectedAgent"
        class="rounded-lg border border-n-weak bg-n-alpha-1 px-2.5 py-1.5 text-[12.5px] text-n-slate-12 outline-none"
      >
        <option value="">{{ L.ALL_AGENTS }}</option>
        <option v-for="agent in agents" :key="agent.id" :value="agent.id">
          {{ agent.name }}
        </option>
      </select>
      <select
        v-model="selectedInbox"
        class="rounded-lg border border-n-weak bg-n-alpha-1 px-2.5 py-1.5 text-[12.5px] text-n-slate-12 outline-none"
      >
        <option value="">{{ L.ALL_INBOXES }}</option>
        <option v-for="inbox in inboxes" :key="inbox.id" :value="inbox.id">
          {{ inbox.name }}
        </option>
      </select>

      <button
        v-if="hasDimensionFilter"
        type="button"
        class="inline-flex items-center gap-1.5 rounded-lg bg-n-brand/10 px-2.5 py-1.5 text-[12.5px] font-medium text-n-brand"
        @click="clearDimensionFilters"
      >
        <span class="i-lucide-x size-3.5" />
        {{ L.CLEAR_FILTER }}
        {{
          reasonFilter ||
          outcomeFilter ||
          classificationLabel(classificationFilter)
        }}
      </button>
    </div>

    <div v-if="loading" class="py-16 text-center text-n-slate-11">
      {{ L.LOADING }}
    </div>
    <div v-else-if="error" class="py-16 text-center text-n-ruby-11">
      {{ error }}
    </div>
    <template v-else-if="summary">
      <!-- KPI tiles -->
      <div class="grid grid-cols-2 gap-3 md:grid-cols-4">
        <div
          class="rounded-2xl border border-n-weak bg-n-solid-1 p-4 shadow-sm"
        >
          <p class="text-[12px] text-n-slate-11">{{ L.KPI_RESOLVES }}</p>
          <p class="mt-1 text-2xl font-semibold tabular-nums text-n-slate-12">
            {{ fmtInt(summary.resolves) }}
          </p>
        </div>
        <div
          class="rounded-2xl border border-n-weak bg-n-solid-1 p-4 shadow-sm"
        >
          <p class="text-[12px] text-n-slate-11">{{ L.KPI_WITH }}</p>
          <p class="mt-1 text-2xl font-semibold tabular-nums text-n-slate-12">
            {{ fmtInt(summary.with_disposition) }}
          </p>
        </div>
        <div
          class="rounded-2xl border border-n-weak bg-n-solid-1 p-4 shadow-sm"
        >
          <p class="text-[12px] text-n-slate-11">{{ L.KPI_COVERAGE }}</p>
          <p class="mt-1 text-2xl font-semibold tabular-nums text-n-slate-12">
            {{ coverage }}%
          </p>
        </div>
        <div
          class="rounded-2xl border border-n-weak bg-n-solid-1 p-4 shadow-sm"
        >
          <p class="text-[12px] text-n-slate-11">{{ L.KPI_AUTO }}</p>
          <p class="mt-1 text-2xl font-semibold tabular-nums text-n-slate-12">
            {{ fmtInt(summary.auto_closed) }}
          </p>
        </div>
      </div>

      <div class="grid gap-4 lg:grid-cols-2">
        <!-- Reasons -->
        <section
          class="rounded-2xl border border-n-weak bg-n-solid-1 p-4 shadow-sm"
        >
          <h2 class="text-sm font-semibold text-n-slate-12">{{ L.REASONS }}</h2>
          <p class="mb-3 mt-0.5 text-[12px] text-n-slate-10">
            {{ L.REASONS_HINT }}
          </p>
          <p v-if="!byReason.length" class="py-6 text-center text-n-slate-10">
            {{ L.EMPTY }}
          </p>
          <button
            v-for="row in byReason"
            :key="row.key"
            type="button"
            class="mb-1.5 block w-full rounded-lg p-1.5 text-start transition-colors hover:bg-n-alpha-1"
            :class="reasonFilter === row.key ? 'bg-n-brand/10' : ''"
            @click="reasonFilter = reasonFilter === row.key ? '' : row.key"
          >
            <div class="flex items-center justify-between gap-2 text-[12.5px]">
              <span class="text-n-slate-12">{{ row.key }}</span>
              <span class="tabular-nums font-medium text-n-slate-11">
                {{ fmtInt(row.count) }}
              </span>
            </div>
            <div class="mt-1 h-1.5 w-full overflow-hidden rounded bg-n-alpha-1">
              <div
                class="h-full rounded bg-n-brand"
                :style="{ width: `${(row.count / maxReason) * 100}%` }"
              />
            </div>
          </button>
        </section>

        <div class="flex flex-col gap-4">
          <!-- Outcomes -->
          <section
            class="rounded-2xl border border-n-weak bg-n-solid-1 p-4 shadow-sm"
          >
            <h2 class="mb-3 text-sm font-semibold text-n-slate-12">
              {{ L.OUTCOMES }}
            </h2>
            <button
              v-for="row in byOutcome"
              :key="row.key"
              type="button"
              class="mb-1.5 block w-full rounded-lg p-1.5 text-start transition-colors hover:bg-n-alpha-1"
              :class="outcomeFilter === row.key ? 'bg-n-brand/10' : ''"
              @click="outcomeFilter = outcomeFilter === row.key ? '' : row.key"
            >
              <div
                class="flex items-center justify-between gap-2 text-[12.5px]"
              >
                <span class="text-n-slate-12">{{ row.key }}</span>
                <span class="tabular-nums font-medium text-n-slate-11">
                  {{ fmtInt(row.count) }}
                </span>
              </div>
              <div
                class="mt-1 h-1.5 w-full overflow-hidden rounded bg-n-alpha-1"
              >
                <div
                  class="h-full rounded bg-n-teal-9"
                  :style="{ width: `${(row.count / maxOutcome) * 100}%` }"
                />
              </div>
            </button>
          </section>

          <!-- Classifications -->
          <section
            class="rounded-2xl border border-n-weak bg-n-solid-1 p-4 shadow-sm"
          >
            <h2 class="mb-3 text-sm font-semibold text-n-slate-12">
              {{ L.CLASSIFICATIONS }}
            </h2>
            <button
              v-for="row in byClassification"
              :key="row.key"
              type="button"
              class="mb-1.5 block w-full rounded-lg p-1.5 text-start transition-colors hover:bg-n-alpha-1"
              :class="classificationFilter === row.key ? 'bg-n-brand/10' : ''"
              @click="
                classificationFilter =
                  classificationFilter === row.key ? '' : row.key
              "
            >
              <div
                class="flex items-center justify-between gap-2 text-[12.5px]"
              >
                <span class="text-n-slate-12">
                  {{ classificationLabel(row.key) }}
                </span>
                <span class="tabular-nums font-medium text-n-slate-11">
                  {{ fmtInt(row.count) }}
                </span>
              </div>
              <div
                class="mt-1 h-1.5 w-full overflow-hidden rounded bg-n-alpha-1"
              >
                <div
                  class="h-full rounded bg-n-amber-9"
                  :style="{
                    width: `${(row.count / maxClassification) * 100}%`,
                  }"
                />
              </div>
            </button>
          </section>
        </div>
      </div>

      <!-- Agents table -->
      <section
        class="rounded-2xl border border-n-weak bg-n-solid-1 p-4 shadow-sm"
      >
        <h2 class="text-sm font-semibold text-n-slate-12">{{ L.AGENTS }}</h2>
        <p class="mb-3 mt-0.5 text-[12px] text-n-slate-10">
          {{ L.AGENTS_HINT }}
        </p>
        <div class="overflow-x-auto">
          <table class="w-full text-[12.5px]">
            <thead>
              <tr class="border-b border-n-weak text-start text-n-slate-10">
                <th class="py-2 text-start font-medium">{{ L.COL_AGENT }}</th>
                <th class="py-2 text-start font-medium">
                  {{ L.COL_RESOLVES }}
                </th>
                <th class="py-2 text-start font-medium">{{ L.COL_WITH }}</th>
                <th class="py-2 text-start font-medium">{{ L.COL_RATE }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in byAgent"
                :key="row.user_id || 'system'"
                class="border-b border-n-weak/60 last:border-0"
              >
                <td class="py-2 text-n-slate-12">{{ row.name }}</td>
                <td class="py-2 tabular-nums text-n-slate-11">
                  {{ fmtInt(row.resolves) }}
                </td>
                <td class="py-2 tabular-nums text-n-slate-11">
                  {{ fmtInt(row.with_disposition) }}
                </td>
                <td class="py-2 tabular-nums">
                  <span
                    :class="
                      rate(row) >= 90
                        ? 'text-n-teal-11'
                        : rate(row) >= 60
                          ? 'text-n-amber-11'
                          : 'text-n-ruby-11'
                    "
                  >
                    {{ rate(row) }}%
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <!-- Daily -->
      <section
        class="rounded-2xl border border-n-weak bg-n-solid-1 p-4 shadow-sm"
      >
        <h2 class="mb-3 text-sm font-semibold text-n-slate-12">
          {{ L.DAILY }}
        </h2>
        <div class="flex flex-wrap items-end gap-2">
          <div
            v-for="row in byDay"
            :key="row.key"
            class="flex flex-col items-center gap-1"
          >
            <span class="text-[11px] tabular-nums text-n-slate-11">
              {{ fmtInt(row.count) }}
            </span>
            <div
              class="w-7 rounded-t bg-n-brand/70"
              :style="{ height: `${Math.max(6, (row.count / maxDay) * 80)}px` }"
            />
            <span class="text-[10px] tabular-nums text-n-slate-10" dir="ltr">
              {{ row.key.slice(5) }}
            </span>
          </div>
        </div>
      </section>
    </template>
  </div>
</template>
